import { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import { useTranslation } from "react-i18next";
import { formatPercentage } from "../../shared/utils/utils";
import { EchartsZoomFrame } from "./echartsZoom";

const LABEL_ROW_HEIGHT = 32;
const CHART_VERTICAL_PADDING = 16;
const MIN_SLOT_COUNT = 8;

const getMaterialBarChartHeight = (itemCount = 0, slotCount = 0) =>
  Math.max(itemCount, slotCount, MIN_SLOT_COUNT) * LABEL_ROW_HEIGHT +
  CHART_VERTICAL_PADDING;

export const countMaterialBarItems = (dataChart = [], labelChart = []) =>
  (dataChart || [])
    .map((value, index) => ({
      value: Number(value) || 0,
      name: labelChart?.[index] ?? "",
    }))
    .filter((item) => item.value > 0 || item.name).length;

/**
 * Horizontal bar chart for material counts (same data API as MaterialCountingPieChart).
 * Bars are sorted descending; colors come from the material palette.
 * Values are displayed as percentages of the total.
 */
export const MaterialCountingHorizontalBarChart = ({
  dataChart = [],
  labelChart = [],
  materialTotal = 0,
  chartTotal,
  colorChart = [],
  showMaterialTotal = false,
  centerLabelVariant = "material",
  title,
  slotCount = 0,
}) => {
  const { t, i18n } = useTranslation();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const percentageDenominator = chartTotal != null ? chartTotal : materialTotal;

  const sortedItems = useMemo(() => {
    const items = (dataChart || []).map((value, index) => {
      const count = Number(value) || 0;
      return {
        value: count,
        percent: Number(formatPercentage(count, percentageDenominator)) || 0,
        percentLabel: `${formatPercentage(count, percentageDenominator)}%`,
        name: labelChart?.[index] ?? "",
        color: colorChart?.[index],
      };
    });
    return items
      .filter((item) => item.value > 0 || item.name)
      .sort((a, b) => b.value - a.value);
  }, [dataChart, labelChart, colorChart, percentageDenominator]);

  const itemCount = sortedItems.length;
  const chartHeight = getMaterialBarChartHeight(itemCount, slotCount);

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
    }
    const chart = chartInstanceRef.current;

    const labels = sortedItems.map((item) => item.name);
    const colors = sortedItems.map((item) => item.color || "#2AB9B8");

    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const point = Array.isArray(params) ? params[0] : params;
          if (!point) return "";
          const item = sortedItems[point.dataIndex];
          if (!item) return "";
          return `${item.name}<br/>${item.percentLabel} (${item.value})`;
        },
      },
      grid: {
        left: 8,
        right: 64,
        top: 8,
        bottom: 8,
        containLabel: true,
      },
      xAxis: {
        type: "value",
        min: 0,
        axisLabel: {
          color: "#222222",
          fontSize: 13,
          formatter: (value) => `${value}%`,
        },
        splitLine: {
          lineStyle: { color: "#F0F0F0" },
        },
      },
      yAxis: {
        type: "category",
        data: labels,
        inverse: true,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          color: "#222222",
          fontSize: 13,
          fontWeight: 500,
          width: 168,
          overflow: "truncate",
          ellipsis: "…",
          lineHeight: 18,
          interval: 0,
        },
      },
      series: [
        {
          type: "bar",
          data: sortedItems.map((item, index) => ({
            value: item.percent,
            itemStyle: {
              color: colors[index],
              borderRadius: [0, 4, 4, 0],
            },
          })),
          barCategoryGap: "32%",
          label: {
            show: true,
            position: "right",
            color: "#222222",
            fontSize: 13,
            fontWeight: 600,
            formatter: (params) =>
              sortedItems[params.dataIndex]?.percentLabel ?? `${params.value}%`,
          },
        },
      ],
    };

    chart.setOption(option, true);
    chart.resize();

    const resizeFn = () => {
      chartInstanceRef.current?.resize();
    };
    window.addEventListener("resize", resizeFn);
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(resizeFn)
        : null;
    if (chartRef.current && resizeObserver) {
      resizeObserver.observe(chartRef.current);
    }

    return () => {
      window.removeEventListener("resize", resizeFn);
      resizeObserver?.disconnect();
    };
  }, [sortedItems, itemCount, percentageDenominator, i18n.language, t]);

  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.resize();
    }
  }, []);

  const totalLabel =
    title ??
    (centerLabelVariant === "errors"
      ? t("Errors detected")
      : materialTotal > 1
        ? t("Detected materials")
        : t("Detected material"));

  return (
    <div className="w-full flex flex-col gap-3">
      {showMaterialTotal ? (
        <div className="flex flex-col items-center text-center flex-shrink-0">
          <span className="text-2xl font-bold text-[#222222]">
            {materialTotal}
          </span>
          <span className="text-sm text-[#5e5e5e]">{totalLabel}</span>
        </div>
      ) : null}
      <EchartsZoomFrame
        chartRef={chartRef}
        className="w-full flex-shrink-0"
        height={chartHeight}
        width="100%"
      />
    </div>
  );
};
